import React from 'react';
import { labelForBehaviour, labelForSemanticRole } from '../adapters';
import { STATUS_COLORS } from '../constants';

const BillStructureCanvas = ({
  draft,
  sectionsSorted,
  selectedFieldCode,
  readOnly,
  activeEditUnlocked = false,
  canUnlockActiveEdit = false,
  onUnlockActiveEdit,
  onSelectField,
  onMoveField,
  onDuplicateField,
  onDeactivateField,
  onOpenAddSection,
  onOpenAddField,
  onOpenPresets,
  onSave,
  saving,
  onCreateVersion,
}) => {
  const isLiveLocked = ['ACTIVE', 'PUBLISHED'].includes(draft?.status) && !activeEditUnlocked;
  const isSuperseded = draft?.status === 'SUPERSEDED';
  const isLocked = readOnly || isSuperseded || isLiveLocked;

  return (
    <div className="panel ra-tpl-canvas">
      <div className="ra-template-toolbar">
        <div>
          <div className="ra-tpl-canvas__title">
            <strong>{draft.name || draft.code}</strong>
            <span
              className="ra-tpl-status-badge"
              style={{ background: `${STATUS_COLORS[draft.status]}22`, color: STATUS_COLORS[draft.status] }}
            >
              {draft.status}
            </span>
            {activeEditUnlocked ? (
              <span className="ra-tpl-status-badge" style={{ background: '#fef3c7', color: '#92400e' }}>
                Editing unlocked
              </span>
            ) : null}
          </div>
          <div className="ra-template-lead">
            {draft.code} · v{draft.version} · {draft.calculationEngine === 'LEGACY_CPWD' ? 'Legacy CPWD' : 'Configurable rules'}
          </div>
        </div>
        <div className="ra-template-toolbar__actions">
          {!isLocked && (
            <>
              <button type="button" className="btn-secondary btn-sm" onClick={onOpenAddSection}>+ Add Section</button>
              <button type="button" className="btn-secondary btn-sm" onClick={onOpenPresets}>+ Common RA Field</button>
              <button type="button" className="btn-primary btn-sm" onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : (activeEditUnlocked ? 'Save changes' : 'Save draft')}
              </button>
            </>
          )}
          {canUnlockActiveEdit && !activeEditUnlocked && (
            <button type="button" className="btn-secondary btn-sm" onClick={onUnlockActiveEdit}>
              Edit current template
            </button>
          )}
          {(isLiveLocked || activeEditUnlocked || isSuperseded) && (
            <button type="button" className="btn-primary btn-sm" onClick={() => onCreateVersion(draft.id)}>
              Create New Version
            </button>
          )}
        </div>
      </div>

      {isLiveLocked && (
        <div className="ra-tpl-lock-banner">
          This template is <strong>{draft.status}</strong>.
          {' '}Use <strong>Edit current template</strong> for quick testing (new bills only),
          or <strong>Create New Version</strong> for a clean production change.
        </div>
      )}
      {activeEditUnlocked && (
        <div className="ra-tpl-lock-banner ra-tpl-lock-banner--edit">
          Editing ACTIVE template in place. Existing RA bills keep their frozen snapshot; new bills use your saved changes.
        </div>
      )}
      {isSuperseded && (
        <div className="ra-tpl-lock-banner">
          This version is <strong>SUPERSEDED</strong> and cannot be edited. Open the active version or create a new one.
        </div>
      )}

      <div className="ra-tpl-canvas-scroll">
        {sectionsSorted.map((sec) => {
          const fields = (draft.fields || [])
            .filter((f) => f.sectionCode === sec.sectionCode)
            .sort((a, b) => a.sequence - b.sequence);
          return (
            <div key={sec.sectionCode} className="ra-template-section-block">
              <div className="ra-template-section-head">
                <div>
                  <strong>{sec.sectionName}</strong>
                  <div className="ra-tpl-section-type">{sec.sectionType || 'CUSTOM'}</div>
                </div>
                {!isLocked && (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => onOpenAddField(sec.sectionCode)}>
                    + Add Field
                  </button>
                )}
              </div>
              {fields.length === 0 ? (
                <div className="ra-tpl-empty-section">No fields in this section yet.</div>
              ) : fields.map((f, idx) => {
                const inactive = String(f.status || 'ACTIVE').toUpperCase() !== 'ACTIVE';
                const cfg = f.metadata?.businessConfig;
                return (
                  <div
                    key={f.fieldCode}
                    className={`ra-template-field-row ${selectedFieldCode === f.fieldCode ? 'active' : ''} ${inactive ? 'is-inactive' : ''}`}
                    onClick={() => onSelectField(f.fieldCode)}
                    draggable={!isLocked}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', f.fieldCode);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = e.dataTransfer.getData('text/plain');
                      if (from && from !== f.fieldCode) onMoveField(from, f.fieldCode);
                    }}
                  >
                    <div className="ra-tpl-field-main">
                      <span className="ra-tpl-line-ref">{cfg?.lineReference || `${idx + 1}.`}</span>
                      <div>
                        <div className="ra-tpl-field-label">{f.fieldLabel || f.fieldCode}</div>
                        <div className="ra-tpl-field-sub">
                          {labelForBehaviour(cfg?.behaviourType)}
                          {f.semanticRole ? ` · ${labelForSemanticRole(f.semanticRole)}` : ''}
                          {cfg?.includeInGroup && cfg.includeInGroup !== 'NONE' ? ` · → ${cfg.includeInGroup.replace(/_/g, ' ')}` : ''}
                        </div>
                      </div>
                    </div>
                    {!isLocked && (
                      <span className="ra-tpl-field-actions" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="btn-secondary btn-sm" title="Move up" onClick={() => onMoveField(f.fieldCode, -1)}>↑</button>
                        <button type="button" className="btn-secondary btn-sm" title="Move down" onClick={() => onMoveField(f.fieldCode, 1)}>↓</button>
                        <button type="button" className="btn-secondary btn-sm" title="Duplicate" onClick={() => onDuplicateField(f.fieldCode)}>⧉</button>
                        <button type="button" className="btn-secondary btn-sm" title={inactive ? 'Activate' : 'Deactivate'} onClick={() => onDeactivateField(f.fieldCode)}>
                          {inactive ? '↩' : '✕'}
                        </button>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {!sectionsSorted.length && (
          <div className="ra-tpl-empty-section">Add a section to start building the RA Bill structure.</div>
        )}
      </div>
    </div>
  );
};

export default BillStructureCanvas;
